import { useState, useCallback } from "react";
import { useAuthStore } from "@/stores/authStore";
import {
  useCreateTenantMutation,
  useTenantQuery,
} from "@/hooks/useTenantQuery";
import type { BuildingStructureBox } from "@/utils/supabase";
import type { TenantInstance } from "@/three/config/types";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";

interface TenantInputFormProps {
  buildingId: string;
  buildingTitle?: string;
  buildingText?: string;
  buildingStructure: BuildingStructureBox[];
  onClose?: () => void;
  /** 입주 성공 시 콜백 */
  onTenantCreated?: () => void;
}

/**
 * 기존 입주자들과 최대한 겹치지 않는 위치를 찾는 함수
 * 거리 기반으로 가장 떨어진 위치를 탐색
 */
function findNonOverlappingPosition(
  existingTenants: TenantInstance[],
  _newTextLength: number = 1
): {
  axis: "x" | "z";
  positionU: number;
  positionV: number;
} {
  // 1. 각 축별 입주자 수 계산
  let xCount = 0;
  let zCount = 0;
  const xTenants: { u: number; v: number }[] = [];
  const zTenants: { u: number; v: number }[] = [];

  for (const tenant of existingTenants) {
    if (tenant.projectionAxis === "x" || tenant.projectionAxis === "-x") {
      xCount++;
      xTenants.push({ u: tenant.positionU, v: tenant.positionV });
    } else {
      zCount++;
      zTenants.push({ u: tenant.positionU, v: tenant.positionV });
    }
  }

  // 2. 입주자가 없으면 완전 랜덤 위치 + 랜덤 축
  if (existingTenants.length === 0) {
    return {
      axis: Math.random() < 0.5 ? "x" : "z",
      positionU: 0.2 + Math.random() * 0.6, // 0.2~0.8 범위
      positionV: 0.2 + Math.random() * 0.6,
    };
  }

  // 3. 축 선택: 입주자가 적은 쪽 우선 (동점이면 랜덤)
  let selectedAxis: "x" | "z";
  if (xCount < zCount) {
    selectedAxis = "x";
  } else if (zCount < xCount) {
    selectedAxis = "z";
  } else {
    selectedAxis = Math.random() < 0.5 ? "x" : "z";
  }

  // 4. 선택된 축의 기존 입주자 목록
  const samAxisTenants = selectedAxis === "x" ? xTenants : zTenants;

  // 5. 해당 축에 입주자가 없으면 랜덤 위치
  if (samAxisTenants.length === 0) {
    return {
      axis: selectedAxis,
      positionU: 0.2 + Math.random() * 0.6,
      positionV: 0.2 + Math.random() * 0.6,
    };
  }

  // 6. 후보 위치 생성 (5x5 그리드)
  const GRID_SIZE = 5;
  const candidates: { u: number; v: number; minDist: number }[] = [];

  for (let gu = 0; gu < GRID_SIZE; gu++) {
    for (let gv = 0; gv < GRID_SIZE; gv++) {
      const u = (gu + 0.5) / GRID_SIZE; // 그리드 중앙
      const v = (gv + 0.5) / GRID_SIZE;

      // 가장 가까운 기존 입주자와의 거리 계산
      let minDist = Infinity;
      for (const tenant of samAxisTenants) {
        const dist = Math.sqrt(
          Math.pow(u - tenant.u, 2) + Math.pow(v - tenant.v, 2)
        );
        minDist = Math.min(minDist, dist);
      }

      candidates.push({ u, v, minDist });
    }
  }

  // 7. 가장 멀리 떨어진 위치 선택 (minDist가 가장 큰 것)
  candidates.sort((a, b) => b.minDist - a.minDist);

  // 상위 3개 중 랜덤 선택 (다양성)
  const topCount = Math.min(3, candidates.length);
  const selected = candidates[Math.floor(Math.random() * topCount)];

  // 8. 약간의 랜덤 오프셋 추가
  const offset = 0.08;
  const finalU = Math.max(
    0.1,
    Math.min(0.9, selected.u + (Math.random() - 0.5) * offset)
  );
  const finalV = Math.max(
    0.1,
    Math.min(0.9, selected.v + (Math.random() - 0.5) * offset)
  );

  return {
    axis: selectedAxis,
    positionU: finalU,
    positionV: finalV,
  };
}

// 계약서 스타일
const contractStyles = {
  container: {
    background: "linear-gradient(180deg, #faf8f0 0%, #f5f0e1 100%)",
    border: "2px solid #8b7355",
    borderRadius: "4px",
    padding: "20px 24px",
    fontFamily: "'Noto Serif KR', 'Georgia', serif",
    boxShadow: `
      0 10px 30px rgba(0,0,0,0.3),
      inset 0 1px 0 rgba(255,255,255,0.4)
    `,
    position: "relative" as const,
    width: "320px", // 너비 축소
  },
  header: {
    textAlign: "center" as const,
    marginBottom: "16px",
    paddingBottom: "12px",
    borderBottom: "2px double #8b7355",
  },
  title: {
    fontSize: "24px", // 제목 폰트 확대
    fontWeight: 700,
    color: "#2a2a2a",
    letterSpacing: "3px",
    margin: 0,
  },
  subtitle: {
    fontSize: "11px",
    color: "#666655",
    marginTop: "4px",
    letterSpacing: "1px",
  },
  body: {
    marginBottom: "20px",
  },
  clause: {
    fontSize: "14px", // 본문 폰트 확대
    color: "#3d3d3d",
    lineHeight: 1.7,
    marginBottom: "16px",
    textAlign: "justify" as const,
  },
  inputContainer: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "6px",
    marginTop: "16px",
  },
  inputLabel: {
    fontSize: "12px",
    color: "#666655",
    fontWeight: 600,
    letterSpacing: "1px",
  },
  input: {
    background: "#fffef9",
    border: "1px solid #c4b9a0",
    borderRadius: "4px",
    padding: "10px 12px",
    fontSize: "14px",
    fontFamily: "'Noto Serif KR', 'Georgia', serif",
    color: "#2a2a2a",
    outline: "none",
    boxShadow: "inset 0 1px 3px rgba(0,0,0,0.05)",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginTop: "24px",
    paddingTop: "16px",
    borderTop: "1px solid #c4b9a0",
  },
  dateSection: {
    fontSize: "12px",
    color: "#888877",
  },
  signSection: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "4px",
  },
  signLabel: {
    fontSize: "11px",
    color: "#666655",
    letterSpacing: "1px",
  },
  fingerprintButton: {
    width: "60px",
    height: "60px",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: 0,
    transition: "all 0.2s ease",
    filter: "grayscale(0.2) contrast(1.1)",
  },
  fingerprintImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain" as const,
    borderRadius: "50%",
  },
  disabledMessage: {
    background: "linear-gradient(180deg, #e8e4dc 0%, #ddd9d0 100%)",
    border: "2px solid #999",
    borderRadius: "4px",
    padding: "24px",
    textAlign: "center" as const,
    fontFamily: "'Noto Serif KR', 'Georgia', serif",
    color: "#666",
    width: "320px",
  },
};

/**
 * 입주 입력 폼 내부 컴포넌트 - 계약서 스타일
 * QueryClientProvider 내부에서만 사용
 */
function TenantInputFormInner({
  buildingId,
  buildingStructure,
  onTenantCreated,
}: TenantInputFormProps) {
  const [tenantText, setTenantText] = useState("");

  // TanStack Query 사용
  const { data: tenants = [] } = useTenantQuery(buildingId);
  const createMutation = useCreateTenantMutation(buildingId);

  const isAnonymous = useAuthStore.getState().isAnonymous();

  // 입주하기
  const handleSubmit = useCallback(async () => {
    if (!tenantText.trim()) return;

    const trimmedText = tenantText.trim();
    // 기존 입주자들과 겹치지 않는 위치 자동 계산 (텍스트 길이 고려)
    const { axis, positionU, positionV } = findNonOverlappingPosition(
      tenants,
      trimmedText.length
    );

    try {
      await createMutation.mutateAsync({
        buildingId,
        text: trimmedText,
        axis,
        positionU,
        positionV,
        structure: buildingStructure,
      });
      setTenantText("");
      onTenantCreated?.();
    } catch (error) {
      console.error("Failed to create tenant:", error);
    }
  }, [
    tenantText,
    tenants,
    buildingId,
    buildingStructure,
    createMutation,
    onTenantCreated,
  ]);

  // Enter 키로 제출
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (
        e.key === "Enter" &&
        !e.nativeEvent.isComposing &&
        tenantText.trim()
      ) {
        handleSubmit();
      }
    },
    [handleSubmit, tenantText]
  );

  // 오늘 날짜
  const today = new Date().toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // 익명 사용자일 경우
  if (isAnonymous) {
    return (
      <div style={contractStyles.disabledMessage}>
        <div style={{ marginBottom: "8px", fontSize: "14px", fontWeight: 600 }}>
          입주 계약서
        </div>
        <div style={{ fontSize: "12px" }}>입주하시려면 로그인이 필요합니다</div>
      </div>
    );
  }

  const isSubmitting = createMutation.isPending;
  const canSubmit = tenantText.trim() && !isSubmitting;

  return (
    <div style={contractStyles.container}>
      {/* 헤더 */}
      <div style={contractStyles.header}>
        <h3 style={contractStyles.title}>입주 계약서</h3>
        <div style={contractStyles.subtitle}>LEASE AGREEMENT</div>
      </div>

      {/* 본문 */}
      <div style={contractStyles.body}>
        <p style={contractStyles.clause}>
          본인은 본 건물에 입주할 것을 서약합니다.
        </p>

        {/* 입력 필드 */}
        <div style={contractStyles.inputContainer}>
          <label style={contractStyles.inputLabel}> 메시지</label>
          <input
            type="text"
            value={tenantText}
            onChange={(e) => setTenantText(e.target.value.slice(0, 50))}
            onKeyDown={handleKeyDown}
            placeholder="메시지를 입력하세요"
            maxLength={50}
            style={contractStyles.input}
          />
        </div>
      </div>

      {/* 푸터: 날짜 & 지문 서명 */}
      <div style={contractStyles.footer}>
        <div style={contractStyles.dateSection}>
          <div>계약일</div>
          <div style={{ fontWeight: 600, color: "#3d3d3d" }}>{today}</div>
        </div>

        <div style={contractStyles.signSection}>
          <span style={contractStyles.signLabel}>날인</span>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              ...contractStyles.fingerprintButton,
              opacity: canSubmit ? 1 : 0.4,
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
            title={canSubmit ? "지문으로 서명하기" : "텍스트를 입력해주세요"}
          >
            <img
              src={import.meta.env.BASE_URL + "/images/jimun.svg"}
              alt="지문 서명"
              style={{
                ...contractStyles.fingerprintImage,
                transform: isSubmitting ? "scale(0.95)" : "scale(1)",
              }}
            />
          </button>
        </div>
      </div>

      {/* 로딩 오버레이 */}
      {isSubmitting && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(250, 248, 240, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "4px",
            fontSize: "14px",
            color: "#666",
          }}
        >
          계약 처리 중...
        </div>
      )}
    </div>
  );
}

/**
 * 입주 입력 폼 컴포넌트 (export)
 * QueryClientProvider로 Inner 컴포넌트를 래핑
 */
export function TenantInputForm(props: TenantInputFormProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <TenantInputFormInner {...props} />
    </QueryClientProvider>
  );
}
